import {
  Component,
  HostListener,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, GlobalService } from '@core/index';
import {
  DateUtils,
  IButton,
  ToasterService,
  TranslateService,
} from '@devinforius/i-essentials';
import { IFile } from '@devinforius/i-file';
import { IViewerConfig } from '@devinforius/i-viewer/lib/models/i-viewer-config';
import { environment } from '@environments/environment';
import { Valve } from '@features/valve/models';
import { ValveService } from '@features/valve/services';
import { Role } from '@shared/enums/roles.enum';
import { StatusEnum } from '@shared/enums/status.enum';
import { Nature, NatureDescription } from '@shared/models';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { DropdownChangeEvent } from 'primeng/dropdown';

@Component({
  selector: 'app-valve-detail',
  templateUrl: './valve-detail.component.html',
  styleUrl: './valve-detail.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ValveDetailComponent implements OnInit {
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private confirmationService: ConfirmationService =
    inject(ConfirmationService);
  private toasterService: ToasterService = inject(ToasterService);
  private translateService: TranslateService = inject(TranslateService);
  private valveService: ValveService = inject(ValveService);
  private globalService: GlobalService = inject(GlobalService);
  private authService: AuthService = inject(AuthService);

  public readonly = false;
  public form!: FormGroup;
  public currentFile!: File | null;
  public currentFileInfo!: (IFile & { src: string }) | null;
  public currentValveId!: number | null;
  public currentValve!: Valve;
  public typeOptions!: {key: string, value: number}[];
  public sourceOptions!: {key: string, value: number}[];
  public natureOptions!: {key: string, value: number}[];
  public natureDescriptionOptions!: {key: string, value: number}[];
  public loadFile: boolean = false;
  public viewerConfig: IViewerConfig = {
    clientId: environment.adobeApiKey,
    embedMode: 'FULL_WINDOW',
    showAnnotationTools: false,
    showThumbnails: false,
    showBookmarks: false
  };
  public buttonsTitle!: IButton[];
  public fileMenuItem: MenuItem[] = [];
  public showStampModal: boolean = false;

  public ngOnInit(): void {
    this.currentValveId =
      parseInt(this.route.snapshot?.paramMap?.get('id') || '0') || null;
    this.initOptions();
    this.loadValve();
  }

  public back(force: boolean = false): void {
    if (force || this.readonly) {
      this.router.navigate(['valves']);
    } else {
      this.confirmationService.confirm({
        message: this.translateService.translate('valves.confirm-back'),
        header: this.translateService.translate('global.confirm'),
        acceptLabel: this.translateService.translate('global.yes'),
        rejectLabel: this.translateService.translate('global.no'),
        accept: () => {
          this.router.navigate(['valves']);
        },
      });
    }
  }

  public openUploadWindow(): void {
    document.getElementById('uploader')?.click();
  }

  public selectFile(event: Event | File): void {
    const file: File | undefined =
      event instanceof File
        ? event
        : (<HTMLInputElement>event.target)?.files?.[0];

    if (file) {
      if (file.size >= 25 * 1024 * 1024) {
        this.toasterService.push(
          'error',
          this.translateService.translate('global.error'),
          this.translateService.translate('valves.form.error.file-size')
        );
      } else {
        if (file.type !== 'application/pdf') {
          this.toasterService.push(
            'error',
            this.translateService.translate('global.error'),
            this.translateService.translate('valves.form.error.file-extension')
          );
        } else {
          this.currentFile = file;
          this.currentFileInfo = {
            extension: 'pdf',
            name: file.name,
            size: (file.size / 1024 / 1024).toFixed(2).toString() + 'Mo',
            src: URL.createObjectURL(file),
          };
        }
      }
    }
  }

  public getSubNature(event: DropdownChangeEvent): void {
    const nature = this.globalService.natures.find(
      (nature: Nature) => nature.id === event.value
    );

    if (nature?.hasNatureDescription) {
      this.loadDescriptionsForNature(event.value);
    } else {
      this.natureDescriptionOptions = [];
    }
  }

  public getControl(key: string): FormControl {
    return this.form.get(key) as FormControl;
  }

  public openStampModal(): void {
    if (this.currentValve.urlTokenTxt && !this.currentValve.log) {
      this.valveService.getStampInformations(this.currentValve.urlTokenTxt).subscribe({
        next: (response: string) => {
          this.currentValve.log = response;
          this.showStampModal = true;
        },
        error: () => {
          this.toasterService.push('error', this.translateService.instant('global.error'), this.translateService.instant('valves.stamp-modal.error'));
        }
      });
    }
  }

  private initOptions(): void {
    this.typeOptions =
      this.globalService.types?.map((item) => ({
        key: item.type,
        value: item.id,
      })) || [];

    this.sourceOptions =
      this.globalService.sources?.map((item) => ({
        key: item.description,
        value: item.id,
      })) || [];

    this.natureOptions =
      this.globalService.natures?.map((item) => ({
        key: item.description,
        value: item.id,
      })) || [];
  }

  private removeFile(): void {
    this.currentFile = null;
    this.currentFileInfo = null;
  }

  private initButtons(force: boolean = false): void {
    this.buttonsTitle = [];
    const statusId = this.currentValve?.statusId?.toString();

    if (!this.currentValve || force) {
      this.buttonsTitle = [
        {
          text: this.translateService.translate('valves.buttons.save'),
          styleClass: 'blue',
          icon: 'bi bi-check-square-fill',
          iconPosition: 'right',
          click: () =>
            this.currentValve ? this.updateValve() : this.saveValve(),
        },
      ];
    } else if (
      statusId === StatusEnum.DEPOSIT ||
      statusId === StatusEnum.TO_CORRECT
    ) {
      this.buttonsTitle = [
        {
          text: this.translateService.translate('valves.buttons.update'),
          styleClass: 'orange',
          icon: 'bi bi-pen-fill',
          iconPosition: 'right',
          click: () => {
            this.readonly = false;
            this.form.enable();

            this.initButtons(true);
          },
        },
      ];

      if (this.authService.haveRole(Role.publisher)) {
        this.buttonsTitle.push({
          text: this.translateService.translate('valves.buttons.publish'),
          styleClass: 'green',
          icon: 'icon-stamp',
          iconPosition: 'right',
          click: () => this.openModalPublish(),
        });
      }
    } else if (statusId === StatusEnum.PUBLISHED && this.authService.haveRole(Role.publisher)) {
      this.buttonsTitle = [
        {
          text: this.translateService.translate('valves.buttons.send-correct'),
          styleClass: 'orange',
          icon: 'bi bi-pen-fill',
          iconPosition: 'right',
          click: () => this.openModalCorrection(),
        },
        {
          text: this.translateService.translate('valves.buttons.archive'),
          styleClass: 'red',
          icon: 'bi bi-archive-fill',
          iconPosition: 'right',
          click: () => this.openModalArchive(),
        },
      ];
    }
  }

  private saveValve(publish: boolean = false): void {
    const form = this.form.getRawValue();
    form.expirationDate = form.expirationDate ? DateUtils.format(form.expirationDate, 'YYYY-MM-DD') : null;
    form.dateDescription = form.dateDescription ? DateUtils.format(form.dateDescription, 'YYYY-MM-DD') : null;

    if (!this.currentFile) {
      this.toasterService.push(
        'error',
        this.translateService.translate('global.error'),
        this.translateService.translate('global.error-file')
      );
      return;
    }

    if (this.form.invalid) {
      this.toasterService.push(
        'error',
        this.translateService.translate('global.error'),
        this.translateService.translate('global.error-form')
      );
      return;
    }

    if (form.descriptionId && !form.dateDescription) {
      this.toasterService.push(
        'error',
        this.translateService.translate('global.error'),
        this.translateService.translate('global.error-form')
      );
      return;
    }

    this.valveService.create(form, this.currentFile as File).subscribe({
      next: () => {
        if (publish) {
          this.openModalPublish();
        } else {
          this.toasterService.push(
            'success',
            this.translateService.translate('global.success'),
            this.translateService.translate('valves.success.create')
          );
          this.back(true);
        }
      },
      error: () => {
        this.toasterService.push(
          'error',
          this.translateService.translate('global.error'),
          this.translateService.translate('valves.error.create')
        );
      },
    });
  }

  private updateValve(publish: boolean = false): void {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    const form = { ...this.form.getRawValue(), id: this.currentValve.id };

    if (regex.test(form.expirationDate)) {
      form.expirationDate = DateUtils.getDate(form.expirationDate);
    }

    form.expirationDate = form.expirationDate ? DateUtils.format(form.expirationDate, 'YYYY-MM-DD') : null;

    if (form.dateDescription && regex.test(form.dateDescription)) {
      form.dateDescription = DateUtils.getDate(form.dateDescription);
    }

    form.dateDescription = form.dateDescription ? DateUtils.format(form.dateDescription, 'YYYY-MM-DD') : null;

    if (this.form.invalid) {
      this.toasterService.push(
        'error',
        this.translateService.translate('global.error'),
        this.translateService.translate('global.error-form')
      );
      return;
    }

    if (form.descriptionId && !form.dateDescription) {
      this.toasterService.push(
        'error',
        this.translateService.translate('global.error'),
        this.translateService.translate('global.error-form')
      );
      return;
    }

    this.valveService.update(form).subscribe({
      next: () => {
        if (publish) {
          this.openModalPublish();
        } else {
          this.toasterService.push(
            'success',
            this.translateService.translate('global.success'),
            this.translateService.translate('valves.success.publish')
          );
          this.back(true);
        }
      },
      error: () => {
        this.toasterService.push(
          'error',
          this.translateService.translate('global.error'),
          this.translateService.translate('valves.error.publish')
        );
      },
    });
  }

  private publishValve(): void {
    this.valveService.publish(this.currentValveId?.toString() || '').subscribe({
      next: () => {
        this.toasterService.push(
          'success',
          this.translateService.translate('global.success'),
          this.translateService.translate('valves.success.publish')
        );
        this.confirmationService.close();
        this.back(true);
      },
      error: (error: Error) => {
        this.toasterService.push(
          'error',
          this.translateService.translate('global.error'),
          this.translateService.translate(
            error.message === 'ERR_ROLE'
              ? 'global.error.role'
              : 'valves.error.publish'
          )
        );
      },
    });
  }

  private archiveValve(): void {
    this.valveService.archive(this.currentValveId?.toString() || '').subscribe({
      next: () => {
        this.toasterService.push(
          'success',
          this.translateService.translate('global.success'),
          this.translateService.translate('valves.success.archived')
        );
        this.confirmationService.close();
      },
      error: (error: Error) => {
        this.toasterService.push(
          'error',
          this.translateService.translate('global.error'),
          this.translateService.translate(
            error.message === 'ERR_ROLE'
              ? 'global.error.role'
              : 'valves.error.archived'
          )
        );
      },
    });
  }

  private sendToCorrection(): void {
    this.valveService
      .unpublish(this.currentValveId?.toString() || '')
      .subscribe({
        next: () => {
          this.toasterService.push(
            'success',
            this.translateService.translate('global.success'),
            this.translateService.translate('valves.success.correction')
          );
          this.confirmationService.close();
        },
        error: (error: Error) => {
          this.toasterService.push(
            'error',
            this.translateService.translate('global.error'),
            this.translateService.translate(
              error.message === 'ERR_ROLE'
                ? 'global.error.role'
                : 'valves.error.correction'
            )
          );
        },
      });
  }

  private loadValve(): void {
    if (this.currentValveId && this.currentValveId >= 0) {
      this.valveService.get(this.currentValveId.toString()).subscribe({
        next: (valve: Valve) => {
          this.readonly = true;
          this.currentValve = valve;
          this.initButtons();
          this.initForm();

          if (valve.natureId && valve.descriptionId) {
            this.loadDescriptionsForNature(valve.natureId.toString(), true);
          }
        },
        error: () => {
          this.toasterService.push(
            'error',
            this.translateService.translate('global.error'),
            this.translateService.translate('valves.error.get-one')
          );
          this.back(true);
        },
      });
    } else {
      this.initButtons();
      this.initForm();

      this.fileMenuItem = [
        {
          label: 'Supprimer',
          icon: 'bi bi-trash3-fill',
          command: () => this.removeFile(),
        },
      ];
    }
  }

  private initForm(): void {
    this.form = new FormGroup({
      title: new FormControl(this.currentValve?.title, Validators.required),
      typeId: new FormControl(this.currentValve?.typeId, Validators.required),
      expirationDate: new FormControl(
        this.currentValve?.expirationDate
          ? DateUtils.format(this.currentValve?.expirationDate)
          : null
      ),
      sourceId: new FormControl(
        this.currentValve?.sourceId,
        Validators.required
      ),
      natureId: new FormControl(
        this.currentValve?.natureId,
        Validators.required
      ),
      descriptionId: new FormControl(this.currentValve?.descriptionId),
      dateDescription: new FormControl(
        this.currentValve?.dateDescription
          ? DateUtils.format(this.currentValve?.dateDescription)
          : null
      ),
    });

    if (this.currentValve) {
      this.form.disable();
      this.getFile();
    }
  }

  private getFile(): void {
    if (this.currentValveId) {
      this.loadFile = true;

      this.valveService.getFile(this.currentValveId.toString()).subscribe({
        next: (file: Blob) => {
          this.currentFile = file as File;

          this.currentFileInfo = {
            extension: 'pdf',
            name: this.currentValve.fileName,
            size: (file.size / 1024 / 1024).toFixed(2).toString() + 'Mo',
            src: window.URL.createObjectURL(file),
          };

          this.loadFile = false;
        },
        error: () => {
          this.loadFile = false;
          this.toasterService.push(
            'error',
            this.translateService.translate('global.error'),
            this.translateService.translate('valves.error.get-file')
          );
        },
      });
    }
  }

  private loadDescriptionsForNature(
    natureId: string,
    load: boolean = false
  ): void {
    this.globalService.getDescriptionByNature(natureId).subscribe({
      next: (response: NatureDescription[]) => {
        console.log('RESPONSE : ', response);
        this.natureDescriptionOptions =
          response.map((item: NatureDescription) => ({
            key: item.description,
            value: item.id,
          })) || [];

        if (!load && this.natureDescriptionOptions.length === 1) {
          this.form
            .get('descriptionId')
            ?.setValue(this.natureDescriptionOptions[0].value);
        }
      },
      error: () => {
        this.natureDescriptionOptions = [];
        this.toasterService.push(
          'error',
          this.translateService.translate('global.error'),
          this.translateService.translate('valves.error.get-nature-description')
        );
      },
    });
  }

  // MODALS
  private openModalPublish(): void {
    this.confirmationService.confirm({
      header: this.translateService.translate('modal.publication.title'),
      message: 'publication',
      key: 'global',
      acceptLabel: this.translateService.translate('modal.publication.accept'),
      acceptVisible: false,
      rejectVisible: false,
      acceptIcon: 'bi bi-cloud-arrow-up-fill',
      accept: () => {
        this.publishValve();
      },
    });
  }

  private openModalCorrection(): void {
    this.confirmationService.confirm({
      header: this.translateService.translate('modal.correction.title'),
      message: 'correction',
      key: 'global',
      acceptLabel: this.translateService.translate('modal.correction.accept'),
      acceptVisible: false,
      rejectVisible: false,
      acceptIcon: 'bi bi-pen-fill',
      accept: () => {
        this.sendToCorrection();
      },
    });
  }

  private openModalArchive(): void {
    this.confirmationService.confirm({
      header: this.translateService.translate('modal.archive.title'),
      message: 'archive',
      key: 'global',
      acceptLabel: this.translateService.translate('modal.archive.accept'),
      acceptVisible: false,
      rejectVisible: false,
      acceptIcon: 'bi bi-archive-fill',
      accept: () => {
        this.archiveValve();
      },
    });
  }

  // Gestion du drag & drop
  public dragAreaClass: string = 'dragarea';

  @HostListener('dragover', ['$event']) onDragOver(event: Event) {
    this.dragAreaClass = 'droparea';
    event.preventDefault();
  }
  @HostListener('dragenter', ['$event']) onDragEnter(event: Event) {
    this.dragAreaClass = 'droparea';
    event.preventDefault();
  }
  @HostListener('dragend', ['$event']) onDragEnd(event: Event) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
  }
  @HostListener('dragleave', ['$event']) onDragLeave(event: Event) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
  }
  @HostListener('drop', ['$event']) onDrop(event: DragEvent) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files) {
      const files: FileList = event.dataTransfer.files;
      this.selectFile(files[0]);
    }
  }
}
